<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <button
      v-if="!print && development"
      class="btn btn-secondary"
      @click="noToAllQuestions()"
    >
      {{ $t('common.noToAllBtn') }}
    </button>

    <div
      v-for="questionGroup in questions"
      :key="questionGroup.underwritingQuestionGroupId"
      class="form-group form-section__group"
    >
      <h4>
        <span v-html="questionGroup.underwritingQuestionGroupName" />
        <br>
        <span
          v-if="currentLocale != 'en'"
          v-html="questionGroup.underwritingQuestionGroupNameTranslation"
        />
      </h4>
      <div class="ps-4 row g-3">
        <div
          v-for="question in questionGroup.underwritingQuestions"
          :key="question.underwritingQuestionId"
        >
          <div class="col-md-12">
            <AppInputCheck
              v-model="question.isAnswer"
              :label="[
                formatQuestionNumber(question.underwritingQuestionNumber, question.underwritingQuestion, question.underwritingQuestionTranslation),
              ]"
              rules="required"
            >
              <div class="ps-2 mt-1">
                <div class="form-check form-check-inline">
                  <input
                    :id="prefix + 'UnderwritingQuestion.Radio-yes_' + question.underwritingQuestionId"
                    v-model="question.isAnswer"
                    class="form-check-input"
                    type="radio"
                    :name="prefix + 'UnderwritingQuestion.Radio_' + question.underwritingQuestionId"
                    :value="true"
                    @change="underwritingAnswerCheck(question, true, questionGroup.underwritingQuestionGroupName)"
                  >

                  <label
                    class="form-check-label"
                    :for="prefix + 'UnderwritingQuestion.Radio-yes_' + question.underwritingQuestionId"
                  >{{ $t('common.yesAnswer') }}</label>
                </div>

                <div class="form-check form-check-inline">
                  <input
                    :id="prefix + 'UnderwritingQuestion.Radio-no_' + question.underwritingQuestionId"
                    v-model="question.isAnswer"
                    class="form-check-input"
                    type="radio"
                    :name="prefix + 'UnderwritingQuestion.Radio_' + question.underwritingQuestionId"
                    :value="false"
                    @change="underwritingAnswerCheck(question, false, questionGroup.underwritingQuestionGroupName)"
                  >

                  <label
                    class="form-check-label"
                    :for="prefix + 'UnderwritingQuestion.Radio-no_' + question.underwritingQuestionId"
                  >{{ $t('common.noAnswer') }}</label>
                </div>
              </div>
            </AppInputCheck>
          </div>

          <!-- Answer of question -->
          <div v-if="question.isAnswer">
            <!-- Just group question == medical declaration -->
            <div v-if="questionGroup.underwritingQuestionGroupName === medicalDeclaration">
              <AnswerAdditionalForm
                :answer="getAnswerByQuestionId(underwritingQuestions, question.underwritingQuestionId, true)"
                :index="prefix + question.underwritingQuestionId"
              />
            </div>
            <!-- Other group question -->
            <div v-else>
              <div
                v-for="(answer, index) in getAnswerByQuestionId(underwritingQuestions, question.underwritingQuestionId)"
                :key="index"
              >
                <AnswerDetailForm
                  :answer="answer"
                  :index="prefix + question.underwritingQuestionId + index"
                  :answer-number="index"
                  :underwriting-question-id="question.underwritingQuestionId"
                  :print="print"
                  @remove="removeAnswer"
                />
              </div>
              <div
                v-if="!print"
                class="d-flex justify-content-end"
              >
                <button
                  class="btn btn-primary mb-5 px-5 py-2 me-3"
                  @click="addMoreAnswer(getAnswerByQuestionId(underwritingQuestions, question.underwritingQuestionId))"
                >
                  {{ $t('common.addConditionBtn') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AnswerDetailForm from './AnswerDetailForm.vue'
import AnswerAdditionalForm from './AnswerAdditionalForm.vue'
import baseApi from '../../api/common/baseApi.js'
import helper from '../../../src/utils/helper.js'
import storage from '../../utils/storage'

export default {
  name: 'QuestionForm',
  components: {
    AnswerDetailForm,
    AnswerAdditionalForm,
  },
  props: {
    underwritingQuestions: { type: Array, required: true },
    prefix: {
      type: String,
      required: true,
    },
    print: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    var testmode = new URLSearchParams(location.search).get('testmode')
    return {
      questions: [],
      development: process.env.NODE_ENV === 'development' || testmode === 'true',
	  currentLocale: this.$locale,
    }
  },
  computed: {
    medicalDeclaration() {
      return this.$t('step4.medicalDeclaration')
    },
  },
  mounted() {
    this.getUnderwritingQuestions()

	if(!this.print) {
		helper.logStepTimestamps(3, storage.getSession('startTime'), storage.getSession('endTime'))
	
		helper.stepLogs('step3', {
			policyHolder: this.$formData.policyHolder,
			familyMembers: this.$formData.familyMembers,
			timestamps: helper.getElapsedTime(storage.getSession('startTime'), storage.getSession('endTime'))
		})
	}
  },
  methods: {
    // Get Question data from api
    async getUnderwritingQuestions() {
      const getParam = new URLSearchParams([
        ['productId', this.$formData.product.productId],
        ['underwritingTypeId', this.$formData.underwritingTypeId],
        ['culture', this.$locale],
        ['selectedDate', this.$formData.policyDetails.policyStartDate],
      ])

      try {
        let resp = await baseApi.get(`api/underwritingQuestions?${getParam}`)
        this.questions = resp
        this.fillQuestionDataForm(resp)
      } catch (error) {
        this.$toast.open({
          message: error,
          type: 'error',
        })
      }
    },

    // Fill data to question display from storage if it have been handled (before)
    fillQuestionDataForm(questions) {
      // re-update questions questions when formData.Question have data
      if (this.underwritingQuestions.length > 0) {
        questions.forEach((questionGroup) => {
          questionGroup.underwritingQuestions.forEach((question) => {
            let questionForm = this.underwritingQuestions.find((q) => q.questionId == question.underwritingQuestionId)

            // Not exist question in formData
            if (!questionForm) {
              let questionForm = {
                questionId: question.underwritingQuestionId,
                questionNumber: question.underwritingQuestionNumber,
                answer: null,
              }
              this.underwritingQuestions.push(questionForm)
            } else {
              // Update when exist
              if (questionForm.isAnswer != null && questionForm.isAnswer != undefined) {
                question.isAnswer = questionForm.isAnswer
                question.answer = questionForm.answer
              } else {
                question.isAnswer = false
              }
            }
          })
        })

        this.$forceUpdate()
        return
      }

      // create new questions for formData.question by questions
      this.questions.forEach((questionGroup) => {
        questionGroup.underwritingQuestions.forEach((question) => {
          let questionForm = {
            questionId: question.underwritingQuestionId,
            questionNumber: question.underwritingQuestionNumber,
            question: question.underwritingQuestion,
            answer: null,
          }

          this.underwritingQuestions.push(questionForm)
        })
      })
    },

    // create answer object when check = true (Yes)
    underwritingAnswerCheck(question, isAnswer, questionGroup = '') {
      let questionForm = this.underwritingQuestions.find((q) => q.questionId == question.underwritingQuestionId)
      if (questionForm == null) {
        this.$toast.open({
          message: 'not found questionId: ' + question.underwritingQuestionId,
          type: 'error',
        })
        return
      }
      if (questionForm.isAnswer !== isAnswer) {
        questionForm.isAnswer = isAnswer
        if (isAnswer === true) {
          if (questionForm.answer == null || question.answer == []) {
            this.initAnswers(questionForm, questionGroup === 'Medical Declaration')
          }
        } else {
          question.answer = null
          questionForm.answer = null
        }
      }

      this.$forceUpdate()
    },

    // Create answers for new question - yes
    initAnswers(question, isMedicalDeclaration) {
      question.answer = []
      question.answer.push(this.createNewAnswer(isMedicalDeclaration))
    },

    // Add more answer
    addMoreAnswer(answerList) {
      answerList.push(this.createNewAnswer(true))
    },

    // Create new obj answer
    createNewAnswer(isMedicalDeclaration = false) {
      return isMedicalDeclaration
        ? {
            // Medical Declaration section
            medicalDeclarationAdditional: '',
          }
        : {
            // Detail section
            nameOfMedicalCondition: '',
            dateStarted: '',
            dateEnded: '',
            medicalTreatment: '',
            currentMedicationName: '',
            hasHospitalRelationToCondition: false, // true,false
            currentConditionId: -1, // List selection,
			currentCondition: ''
          }
    },

    removeAnswer(underwritingQuestionId, removeIndex) {
      let answerList = this.getAnswerByQuestionId(this.underwritingQuestions, underwritingQuestionId)
      if (answerList != [] && answerList.length - 1 >= removeIndex) {
        answerList.splice(removeIndex, 1)
        this.$forceUpdate()
      }
    },

    getAnswerByQuestionId(underwritingQuestions, questionId, isMedical = false) {
      let answers = underwritingQuestions.find((q) => q.questionId == questionId).answer
      if (isMedical) {
        return answers[0]
      }

      return answers
    },

    // Test only
    noToAllQuestions() {
      this.questions.forEach((group) => {
        group.underwritingQuestions.forEach((question) => {
          question.isAnswer = false
          this.underwritingAnswerCheck(question, false)
        })
      })

      this.$forceUpdate()
    },

    formatQuestionNumber(num, original, translation) {
      const dec = num.toString().split('.')[1]
      if (dec && dec.length == 1) {
        num = Number(num).toFixed(2)
      }

	  if(this.$locale == 'en') {
		  return num + '. ' + original
	  } else {
		  return num + '. ' + original + '<br>' + translation
	  }
    },
  },
}
</script>
